import React, { useState } from 'react';
import { GripVertical, MoveUp, MoveDown, Edit2, X, Check, Trash2 } from 'lucide-react';

interface Process {
  id: string;
  name: string;
  step_number: string;
  assignee?: string;
  description?: string;
  [key: string]: any;
}

interface ReorderableProcessListProps {
  processes: Process[];
  onReorder: (reorderedProcesses: Process[]) => void;
  availableStakeholders?: string[];
  onStepEdit?: (index: number, updatedStep: Process) => void;
  onStepDelete?: (index: number, step: Process) => void;
}

const ReorderableProcessList: React.FC<ReorderableProcessListProps> = ({
  processes,
  onReorder,
  availableStakeholders = [],
  onStepEdit,
  onStepDelete
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{
    name: string;
    description: string;
    assignee: string;
  }>({ name: '', description: '', assignee: '' });
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    index: number;
    step: Process | null;
  }>({ isOpen: false, index: -1, step: null });

  const isStartStep = (index: number) => index === 0;
  const isEndStep = (index: number) => index === processes.length - 1;
  const isLockedStep = (index: number) => isStartStep(index) || isEndStep(index);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (isLockedStep(index)) {
      e.preventDefault();
      return;
    }
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    if (isLockedStep(index)) return;
    setDragOverIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    if (isLockedStep(dropIndex)) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newProcesses = [...processes];
    const draggedItem = newProcesses[draggedIndex];

    newProcesses.splice(draggedIndex, 1);

    let finalDropIndex = dropIndex;
    if (draggedIndex < dropIndex) {
      finalDropIndex = dropIndex - 1;
    }

    if (finalDropIndex === 0) {
      finalDropIndex = 1;
    } else if (finalDropIndex >= processes.length - 1) {
      finalDropIndex = processes.length - 2;
    }

    newProcesses.splice(finalDropIndex, 0, draggedItem);

    onReorder(newProcesses);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const moveUp = (index: number) => {
    if (index <= 1 || isLockedStep(index)) return;
    const newProcesses = [...processes];
    [newProcesses[index - 1], newProcesses[index]] = [newProcesses[index], newProcesses[index - 1]];
    onReorder(newProcesses);
  };

  const moveDown = (index: number) => {
    if (index >= processes.length - 2 || isLockedStep(index)) return;
    const newProcesses = [...processes];
    [newProcesses[index], newProcesses[index + 1]] = [newProcesses[index + 1], newProcesses[index]];
    onReorder(newProcesses);
  };

  const handleEditClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const step = processes[index];
    setEditingIndex(index);
    setEditForm({
      name: step.name || '',
      description: step.description || step.full_text || '',
      assignee: step.assignee || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditForm({ name: '', description: '', assignee: '' });
  };

  const handleSaveEdit = (index: number) => {
    const updatedStep = {
      ...processes[index],
      name: editForm.name,
      description: editForm.description,
      full_text: editForm.description,
      assignee: editForm.assignee
    };

    if (onStepEdit) {
      onStepEdit(index, updatedStep);
    } else {
      const newProcesses = [...processes];
      newProcesses[index] = updatedStep;
      onReorder(newProcesses);
    }

    setEditingIndex(null);
    setEditForm({ name: '', description: '', assignee: '' });
  };

  const handleDeleteClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLockedStep(index)) return;
    setDeleteConfirmation({
      isOpen: true,
      index,
      step: processes[index]
    });
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmation.step && onStepDelete) {
      onStepDelete(deleteConfirmation.index, deleteConfirmation.step);
    }
    setDeleteConfirmation({ isOpen: false, index: -1, step: null });
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, index: -1, step: null });
  };

  return (
    <>
      <div className="space-y-2">
        {processes.map((process, index) => {
          const locked = isLockedStep(index);
          const isEditing = editingIndex === index;

          return (
          <div
            key={process.id}
            className={`
              rounded-lg border-2 transition-all
              ${locked ? 'bg-gray-100 border-gray-300 opacity-60' : 'bg-white'}
              ${draggedIndex === index ? 'opacity-50 border-electric-blue bg-blue-50' : ''}
              ${dragOverIndex === index && draggedIndex !== index && !locked ? 'border-green-500 bg-green-50' : ''}
              ${!locked && !draggedIndex && !dragOverIndex && !isEditing ? 'border-gray-200 hover:shadow-md' : ''}
              ${isEditing ? 'border-blue-500 shadow-lg' : ''}
            `}
          >
            {!isEditing && (
              <div
                draggable={!locked}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnter={(e) => handleDragEnter(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-center space-x-3 p-3 ${locked ? 'cursor-not-allowed' : 'cursor-move'}`}
              >
                <GripVertical className={`w-5 h-5 flex-shrink-0 ${locked ? 'text-gray-300' : 'text-gray-400'}`} />

                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {process.name}
                  </div>
                  {process.assignee && (
                    <div className="text-xs text-gray-500 truncate">
                      {process.assignee}
                    </div>
                  )}
                </div>

                {!locked && (
                  <div className="flex items-center space-x-1 flex-shrink-0">
                    <button
                      onClick={(e) => handleEditClick(index, e)}
                      className="p-1 rounded hover:bg-blue-100 transition-colors"
                      title="Edit step"
                    >
                      <Edit2 className="w-4 h-4 text-blue-600" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteClick(index, e)}
                      className="p-1 rounded hover:bg-red-100 transition-colors"
                      title="Delete step"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                    <button
                      onClick={() => moveUp(index)}
                      disabled={index <= 1}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <MoveUp className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                      onClick={() => moveDown(index)}
                      disabled={index >= processes.length - 2}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <MoveDown className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                )}

                <div className="flex items-center space-x-2 flex-shrink-0">
                  {locked && (
                    <span className="text-xs font-medium text-gray-500 bg-gray-200 px-2 py-1 rounded">
                      {isStartStep(index) ? 'START' : 'END'}
                    </span>
                  )}
                  <div className="text-sm font-mono text-gray-500">
                    #{index}
                  </div>
                </div>
              </div>
            )}

            {isEditing && (
              <div className="p-4 space-y-3 bg-blue-50">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-900">Edit Step #{index}</h4>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={(e) => handleDeleteClick(index, e)}
                      className="p-1.5 rounded bg-red-500 hover:bg-red-600 text-white transition-colors"
                      title="Delete step"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleSaveEdit(index)}
                      className="p-1.5 rounded bg-green-500 hover:bg-green-600 text-white transition-colors"
                      title="Save changes"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="p-1.5 rounded bg-gray-400 hover:bg-gray-500 text-white transition-colors"
                      title="Cancel"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Step Name
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter step name"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter step description"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Assignee / Stakeholder
                  </label>
                  {availableStakeholders.length > 0 ? (
                    <select
                      value={editForm.assignee}
                      onChange={(e) => setEditForm({ ...editForm, assignee: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select stakeholder...</option>
                      {availableStakeholders.map((stakeholder) => (
                        <option key={stakeholder} value={stakeholder}>
                          {stakeholder}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={editForm.assignee}
                      onChange={(e) => setEditForm({ ...editForm, assignee: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter assignee name"
                    />
                  )}
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>

      {deleteConfirmation.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-red-100">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Delete Step</h2>
              </div>
              <button
                onClick={handleCancelDelete}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-700 leading-relaxed">
                Are you sure you want to delete this step?
              </p>
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900 text-sm">
                  {deleteConfirmation.step?.name}
                </p>
              </div>
              <p className="text-sm text-gray-500 mt-3">
                This action will remove the step and automatically renumber all remaining steps.
                Connections to this step will be redirected to the next step in the sequence.
              </p>
            </div>
            <div className="flex gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={handleCancelDelete}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Delete Step
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReorderableProcessList;
